import React, { Dispatch, SetStateAction, startTransition, useCallback, useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { BrandKitsAPI } from '@makyoapp/makyo-sdk'

import { queryKeys } from '@/lib/react-query'
import { toSdkBrandKitBody } from '@/adapters/__project__/BrandKitAdapter'
import { useGetBrandKitDetail } from '@/resolvers/org/brand-kit/query'
import { useParamId } from '@/hooks/useParamId'
import {
  BrandKit,
  BrandKitColor,
  DEFAULT_EXTENDED_COLORS,
  MultiSourceTheme,
  themeFromMultiSource,
} from '@/shared/brand-kit'
import { MAX_EXTENDED_COLORS } from '@/shared/constants'

import { useBrandKitContext } from './BrandKitContext'

interface BrandKitViewToken {
  name: string
  theme: MultiSourceTheme
}

interface BrandKitTheme {
  name: string
  theme: MultiSourceTheme
}

interface BrandKitCpColor {
  name: string
  themeName?: string
  target: string
  color: string
  index?: number
  blend?: boolean
  hideBlend?: boolean
}

interface BrandKitDetailContextValue {
  /**
   * current active brand kit
   */
  brand?: BrandKit
  /**
   * loading brand kit indicator
   */
  isLoading: boolean
  /**
   * color picker state, if defined it will show color picker modal
   */
  cpColor?: BrandKitCpColor
  /**
   * set color picker state
   */
  setCpColor: Dispatch<SetStateAction<BrandKitCpColor | undefined>>
  /**
   * list of themes
   */
  themes: BrandKitTheme[]
  /**
   * set theme list
   */
  setThemes: Dispatch<SetStateAction<BrandKitTheme[]>>
  /**
   * if defined it will show color token modal based on the value provided
   */
  viewToken?: BrandKitViewToken
  /**
   * set view token
   */
  setViewToken: Dispatch<SetStateAction<BrandKitViewToken | undefined>>
  /**
   * update brand detail, this will also call update on global `BrandDetailContext`
   */
  updateBrandDetail: (next: BrandKit) => void
  /**
   * handle update font
   */
  handleUpdateFont: (next: BrandKit) => Promise<null>
  /**
   * current brandkit theme index
   */
  currentThemeIdx: string
  /**
   * set current brandkit theme index
   */
  setCurrentThemeIdx: (next: string) => void
}

const BrandKitDetailContext = React.createContext<BrandKitDetailContextValue>({
  brand: undefined,
  cpColor: undefined,
  setCpColor: () => null,
  isLoading: false,
  viewToken: undefined,
  setViewToken: () => null,
  updateBrandDetail: () => null,
  handleUpdateFont: async () => null,
  themes: [],
  setThemes: () => null,
  currentThemeIdx: '0',
  setCurrentThemeIdx: () => null,
})

/**
 * Brand Kit Detail Provider
 */
export function BrandKitDetailProvider({
  brandId: brandIdProp,
  children,
}: {
  brandId?: string
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()

  const brandParam = useParamId('brand')
  const brandId = brandIdProp ? brandIdProp : brandParam

  // Access updater from brandkit list context
  const { onUpdateBrand } = useBrandKitContext()

  // Access brand kit detail
  const { data: brand, isLoading } = useGetBrandKitDetail(brandId)

  // Derived data from brandkit detail
  const { colors } = brand || {}

  // Context State
  const [currentThemeIdx, setCurrentThemeIdx] = useState('0')
  const [themes, setThemes] = useState<BrandKitTheme[]>([])
  const [cpColor, setCpColor] = useState<BrandKitCpColor | undefined>(undefined)
  const [viewToken, setViewToken] = useState<BrandKitViewToken | undefined>(undefined)

  const handleUpdateFont = useCallback(async (next: BrandKit) => {
    return BrandKitsAPI.ReqUpdate(next.id, toSdkBrandKitBody(next))
  }, [])

  const handleUpdateThemes = useCallback((colors: BrandKitColor[]) => {
    const updatedThemes = colors.map((c) => ({
      name: c.name,
      theme: themeFromMultiSource([c.value], c.customColors ?? []),
    }))
    setThemes(updatedThemes)
  }, [])

  const updateBrandDetail = useCallback(
    (brand: BrandKit) => {
      startTransition(() => {
        queryClient.setQueryData([queryKeys.orgBrandKitDetail, brandId], brand)
        handleUpdateThemes(brand.colors)
        onUpdateBrand(brand.id, brand)
      })
    },
    [brandId, handleUpdateThemes, onUpdateBrand, queryClient]
  )

  useEffect(() => {
    if (colors && colors.length > 0) {
      handleUpdateThemes(colors)
    }
  }, [colors, handleUpdateThemes])

  // make sure extended color length is 12
  useEffect(() => {
    if (!brand) return

    const extendedColors = brand.colors[0].customColors || []

    if (extendedColors.length !== MAX_EXTENDED_COLORS) {
      const updatedBrand = { ...brand }

      updatedBrand.colors[0].customColors = [...DEFAULT_EXTENDED_COLORS].map((c) => ({
        value: c._Value,
        name: c._Name,
        blend: c._Blend,
      }))

      BrandKitsAPI.ReqUpdate(brand.id, toSdkBrandKitBody(updatedBrand)).then(() => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitDetail, brand.id] })
      })
    }
  }, [brand, colors, queryClient])

  return (
    <BrandKitDetailContext.Provider
      value={{
        brand,
        isLoading,
        viewToken,
        setViewToken,
        cpColor,
        setCpColor,
        themes,
        setThemes,
        updateBrandDetail,
        handleUpdateFont,
        currentThemeIdx,
        setCurrentThemeIdx,
      }}
    >
      {children}
    </BrandKitDetailContext.Provider>
  )
}

/**
 * Brand Kit Detail Context
 *
 * use this context to access and modify current brand kit
 */
export function useBrandKitDetailContext() {
  const context = useContext(BrandKitDetailContext)

  if (context === undefined) {
    throw new Error('useBrandKitDetailContext must be used within a BrandKitDetailProvider')
  }

  return context
}
